import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { IconeBaixo, IconeMensagem, IconeSair } from "../icones";
import { LinkInterno } from "../navegacao";
import { AlternarTema } from "../tema";
import { Flutuante, GAVETA_DESDE, useMidia } from "./Flutuante";
import { MarcaVisual, type Marca } from "./TelaDeAcesso";

export type ItemDeMenu = { rotulo: string; href: string; atual?: boolean };
/** Um grupo do menu lateral do painel (Financeiro, Comercial, Cadastros...): abre e fecha, com os itens dele. */
export type GrupoDeMenu = { rotulo: string; itens: ItemDeMenu[] };
export type EntradaDeMenu = ItemDeMenu | GrupoDeMenu;
const ehGrupo = (e: EntradaDeMenu): e is GrupoDeMenu => "itens" in e;

/** Celular: a lateral vira barra no topo e o grupo abre por cima (mesmo corte do CSS). */
const CELULAR = "(max-width: 860px)";
/** Se a tela esta no corte do celular agora (acompanha girar o aparelho e redimensionar a janela). */
function useCelular(): boolean {
  const [celular, setCelular] = useState(() => typeof window !== "undefined" && window.matchMedia(CELULAR).matches);
  useEffect(() => {
    const m = window.matchMedia(CELULAR);
    const mudou = () => setCelular(m.matches);
    m.addEventListener("change", mudou);
    return () => m.removeEventListener("change", mudou);
  }, []);
  return celular;
}

/**
 * Grupo do menu do painel. Na lateral e um acordeao de um grupo so (Vitor, 13/10: "quando eu estou em um menu dropdown,
 * por exemplo financeiro, todos os outros devem ficar fechados, ate eu clicar no proximo e o financeiro tbm fecha"):
 * quem decide qual esta aberto e a `CascaPainel`. Fechado com a pagina atual dentro, o rotulo fica marcado. No celular
 * abre por cima da tela (a barra rola de lado e cortaria a lista) e fecha ao escolher, no Esc ou tocando fora.
 */
function GrupoDoMenu({ grupo, naLateral, aoAlternar }: { grupo: GrupoDeMenu; naLateral: boolean; aoAlternar: () => void }) {
  const celular = useCelular();
  const temAtual = grupo.itens.some((i) => i.atual);
  const [flutuante, setFlutuante] = useState<{ x: number; y: number } | null>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  const id = useId();
  const aberto = celular ? Boolean(flutuante) : naLateral;

  useEffect(() => { if (!celular) setFlutuante(null); }, [celular]);
  useEffect(() => {
    if (!flutuante) return;
    const fora = (e: PointerEvent) => { if (!lista.current?.contains(e.target as Node) && !botao.current?.contains(e.target as Node)) setFlutuante(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { setFlutuante(null); botao.current?.focus(); } };
    const rolou = () => setFlutuante(null);
    document.addEventListener("pointerdown", fora);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", rolou, { passive: true });
    return () => { document.removeEventListener("pointerdown", fora); document.removeEventListener("keydown", esc); window.removeEventListener("scroll", rolou); };
  }, [flutuante]);

  const alternar = () => {
    if (celular) {
      const r = botao.current?.getBoundingClientRect();
      setFlutuante(flutuante || !r ? null : { x: Math.max(8, Math.min(r.left, window.innerWidth - 248)), y: r.bottom + 4 });
      return;
    }
    aoAlternar();
  };

  const links = grupo.itens.map((i) => <LinkInterno key={i.href} href={i.href} aria-current={i.atual ? "page" : undefined}>{i.rotulo}</LinkInterno>);
  return (
    <div className={`mtc-grupo${aberto ? " mtc-grupo--aberto" : ""}${temAtual ? " mtc-grupo--atual" : ""}`}>
      <button ref={botao} type="button" className="mtc-grupo__botao" aria-expanded={aberto} aria-controls={id} onClick={alternar}>
        <span>{grupo.rotulo}</span>
        <IconeBaixo tamanho={16} />
      </button>
      {celular
        ? flutuante && <div ref={lista} id={id} className="mtc-grupo__itens mtc-grupo__itens--flutuante" style={{ left: flutuante.x, top: flutuante.y }} onClick={() => setFlutuante(null)}>{links}</div>
        : naLateral && <div id={id} className="mtc-grupo__itens">{links}</div>}
    </div>
  );
}

/** Ponto de pendencia num destino (spec 038, secao 4): o backend diz o tom e o motivo; a casca so desenha. */
export type PontoDePendencia = { tom: "alerta" | "erro" | "novo"; motivo?: string | null };
/** Um destino da casca da area: rotulo, icone de 24 px e, quando ha, o ponto de pendencia. */
export type DestinoDaArea = ItemDeMenu & {
  icone?: ReactNode;
  ponto?: PontoDePendencia | null;
  /** No celular a barra de baixo cabe quatro destinos com o rotulo inteiro: este fica fora dela (a tela leva por atalho). */
  foraDaBarra?: boolean;
};

/**
 * Um destino: icone com o indicador em pilula do Material 3 e o rotulo. O motivo do ponto vai pra descricao
 * (`aria-describedby`), nao pro nome: o nome do link continua sendo so o rotulo, que e o que o e2e e o leitor procuram.
 */
function Destino({ d }: { d: DestinoDaArea }) {
  const id = useId();
  const motivo = d.ponto ? d.ponto.motivo || "Tem novidade aqui" : null;
  return (
    <>
      <LinkInterno href={d.href} className={`mtc-destino${d.foraDaBarra ? " mtc-destino--fora-da-barra" : ""}`} aria-current={d.atual ? "page" : undefined} {...(motivo ? { "aria-describedby": id, title: motivo } : {})}>
        <span className="mtc-destino__icone">
          {d.icone}
          {d.ponto && <span className={`mtc-destino__ponto mtc-destino__ponto--${d.ponto.tom}`} aria-hidden="true" />}
        </span>
        <span className="mtc-destino__rotulo">{d.rotulo}</span>
      </LinkInterno>
      {motivo && <span id={id} hidden>{motivo}</span>}
    </>
  );
}

const inicialDe = (nome: string) => (nome.trim().slice(0, 1) || "?").toUpperCase();

/**
 * A conta no trilho e no celular: o avatar abre o `Flutuante` (folha inferior no celular) com Perfil, Certificados,
 * "Falar com a equipe", o tema e Sair. Na gaveta do desktop isso tudo fica a vista, sem botao.
 */
function ContaCompacta({ nome, itens, falar, aoSair }: { nome: string; itens: DestinoDaArea[]; falar?: string; aoSair?: () => void }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div className="mtc-conta">
      <button type="button" className="mtc-conta__botao" aria-label="Sua conta" aria-haspopup="dialog" aria-expanded={aberto} onClick={() => setAberto(!aberto)}>
        <span className="mtc-conta__inicial" aria-hidden="true">{inicialDe(nome)}</span>
      </button>
      <Flutuante aberto={aberto} aoFechar={() => setAberto(false)} titulo="Sua conta" descricao={nome} classe="mtc-conta__lista">
        <nav aria-label="Itens da conta" className="mtc-conta__itens">
          {itens.map((i) => (
            <LinkInterno key={i.href} href={i.href} className="mtc-conta__item" aria-current={i.atual ? "page" : undefined} onClick={() => setAberto(false)}>{i.icone}{i.rotulo}</LinkInterno>
          ))}
          {falar && <a className="mtc-conta__item" href={falar} target="_blank" rel="noreferrer"><IconeMensagem tamanho={20} />Falar com a equipe</a>}
        </nav>
        <div className="mtc-conta__tema"><AlternarTema comRotulo /></div>
        {aoSair && <button type="button" className="mtc-conta__item mtc-conta__item--sair" onClick={() => { setAberto(false); aoSair(); }}><IconeSair tamanho={20} />Sair</button>}
      </Flutuante>
    </div>
  );
}

/**
 * Casca da area do cliente (spec 038, secao 2). **Sem barra no topo** (Vitor, 24/09: "no header nao teria nada
 * mesmo"); o que muda e a largura:
 *
 * - **Desktop (1024 px ou mais): gaveta fixa de 264 px.** Logo e sino no topo (o painel do sino abre pra baixo e pra
 *   direita, dentro da janela), o cartao do plano atual, os destinos com icone e ponto de pendencia, o grupo "Sua
 *   conta" (Perfil e Certificados como itens de verdade) e o rodape com "Falar com a equipe", o tema, o avatar e Sair.
 * - **Tablet (600 a 1023 px): trilho de 80 px.** Logo em cima, destinos com o rotulo embaixo do icone, sino e avatar
 *   no pe (abrem ao lado do trilho). O cartao do plano vira faixa no topo do conteudo.
 * - **Celular (abaixo de 600 px): barra inferior de 80 px** com os destinos, o sino e o avatar. Sino, conta e seletor
 *   de plano abrem como folha inferior (spec 038, secao 6: o topo continua vazio). O plano vira faixa no conteudo.
 *
 * Cada peca existe uma vez so no HTML e o CSS muda o lugar dela (a grade da lateral troca de desenho por largura), menos o
 * cartao do plano, que troca de lugar (gaveta ou topo do conteudo) e por isso e montado so onde aparece.
 * `nav[aria-label="Menu"]` segue valendo em todas as larguras (o e2e procura os destinos por ali).
 */
export function CascaArea({ marca, itens, usuario, conta, aoSair, sino, plano, falarComEquipe, children }: {
  marca: Marca;
  itens: DestinoDaArea[];
  usuario?: { nome: string };
  /** O que e da pessoa (Perfil, Certificados): grupo "Sua conta" na gaveta, dentro da conta no trilho e no celular. */
  conta?: DestinoDaArea[];
  aoSair?: () => void;
  /** O sino (o app busca as notificacoes). */
  sino?: ReactNode;
  /** O cartao do plano atual; recebe a variante do lugar onde vai aparecer. */
  plano?: (variante: "gaveta" | "faixa") => ReactNode;
  /** Link do WhatsApp da equipe, ja com a mensagem pronta. */
  falarComEquipe?: string;
  children: ReactNode;
}) {
  const gaveta = useMidia(GAVETA_DESDE);
  const contaItens = conta ?? [];
  return (
    <div className="mtc-casca-area">
      <aside className="mtc-area-lateral">
       {/* O interno gruda no topo (`sticky`) enquanto o `aside` acompanha a altura da coluna: em pagina longa a
           lateral fica parada, e em pagina curta o rodape encosta no fim de verdade. */}
       <div className="mtc-area-lateral__interno">
        <LinkInterno href="/" className="mtc-area-lateral__marca"><MarcaVisual marca={marca} classe="" /></LinkInterno>
        {sino && <div className="mtc-area-lateral__sino">{sino}</div>}
        {gaveta && plano && <div className="mtc-area-lateral__plano">{plano("gaveta")}</div>}
        <div className="mtc-area-lateral__rolagem">
          <nav className="mtc-area-nav" aria-label="Menu">
            {itens.map((d) => <Destino key={d.href} d={d} />)}
          </nav>
          {contaItens.length > 0 && (
            <nav className="mtc-area-grupo" aria-label="Sua conta">
              <span className="mtc-area-grupo__titulo" aria-hidden="true">Sua conta</span>
              {contaItens.map((d) => <Destino key={d.href} d={d} />)}
            </nav>
          )}
        </div>
        <div className="mtc-area-lateral__rodape">
          {falarComEquipe && (
            <a className="mtc-destino mtc-destino--externo" href={falarComEquipe} target="_blank" rel="noreferrer">
              <span className="mtc-destino__icone"><IconeMensagem tamanho={22} /></span>
              <span className="mtc-destino__rotulo">Falar com a equipe</span>
            </a>
          )}
          <div className="mtc-area-quem">
            {usuario && <span className="mtc-conta__inicial" aria-hidden="true">{inicialDe(usuario.nome)}</span>}
            {usuario && <span className="mtc-area-quem__nome" title={usuario.nome}>{usuario.nome}</span>}
            <AlternarTema />
            {aoSair && <button type="button" className="mtc-botao-icone" aria-label="Sair" title="Sair" onClick={aoSair}><IconeSair tamanho={20} /></button>}
          </div>
        </div>
        {usuario ? <div className="mtc-area-lateral__conta"><ContaCompacta nome={usuario.nome} itens={contaItens} falar={falarComEquipe} aoSair={aoSair} /></div> : <div className="mtc-area-lateral__conta"><AlternarTema /></div>}
       </div>
      </aside>
      <main className="mtc-conteudo">
        {!gaveta && plano && <div className="mtc-area-faixa">{plano("faixa")}</div>}
        {children}
      </main>
    </div>
  );
}

const CHAVE_DA_LATERAL = "mtc-painel-lateral";
function lateralRecolhidaSalva(): boolean {
  try {
    return localStorage.getItem(CHAVE_DA_LATERAL) === "recolhida";
  } catch {
    return false;
  }
}

/**
 * Casca do painel: menu lateral com a marca, `acoes` (o sino) ao lado dela, o menu do ambiente e o operador logado.
 * No celular a lateral vira barra no topo e o menu rola de lado. No computador ela recolhe num trilho estreito so com o
 * sino, o tema e o Sair (Vitor, 27/10); o botao de abrir e recolher fica na borda, e o aparelho lembra a escolha.
 */
export function CascaPainel({ marca, itens, usuario, aoSair, extras, acoes, children }: { marca: Marca; itens: EntradaDeMenu[]; usuario?: { nome: string; papel: string }; aoSair?: () => void; extras?: ReactNode; acoes?: ReactNode; children: ReactNode }) {
  // Um grupo aberto por vez. Comeca (e volta, a cada pagina nova) com o grupo da pagina atual, ou com todos fechados.
  const grupos = itens.filter(ehGrupo);
  const atual = grupos.flatMap((g) => g.itens).find((i) => i.atual)?.href ?? null;
  const grupoAtual = grupos.find((g) => g.itens.some((i) => i.atual))?.rotulo ?? null;
  const [aberto, setAberto] = useState<string | null>(grupoAtual);
  useEffect(() => { setAberto(grupoAtual); }, [atual, grupoAtual]);
  const [recolhida, setRecolhida] = useState(lateralRecolhidaSalva);
  const alternarLateral = () => {
    const nova = !recolhida;
    setRecolhida(nova);
    try {
      if (nova) localStorage.setItem(CHAVE_DA_LATERAL, "recolhida");
      else localStorage.removeItem(CHAVE_DA_LATERAL);
    } catch {
      // sem localStorage (aba anonima restrita): vale so nesta tela
    }
  };
  const menuId = useId();
  return (
    <div className={`mtc-casca-painel${recolhida ? " mtc-casca-painel--recolhida" : ""}`}>
      <aside className="mtc-lateral">
        <div className="mtc-lateral__topo">
          <LinkInterno href="/" className="mtc-lateral__marca">
            <MarcaVisual marca={marca} classe="" />
            <span className="mtc-rotulo">Painel de operação</span>
          </LinkInterno>
          <div className="mtc-lateral__acoes">{acoes}<AlternarTema /></div>
        </div>
        {/* Na borda da lateral, como uma aba: nao disputa o topo com a marca, o sino e o tema. */}
        <button type="button" className="mtc-lateral__recolher" aria-label={recolhida ? "Abrir o menu" : "Recolher o menu"} title={recolhida ? "Abrir o menu" : "Recolher o menu"} aria-expanded={!recolhida} aria-controls={menuId} onClick={alternarLateral}><IconeBaixo tamanho={16} /></button>
        <nav id={menuId} aria-label="Menu">
          {itens.map((e) => (ehGrupo(e)
            ? (e.itens.length ? <GrupoDoMenu key={`grupo:${e.rotulo}`} grupo={e} naLateral={aberto === e.rotulo} aoAlternar={() => setAberto((a) => (a === e.rotulo ? null : e.rotulo))} /> : null)
            : <LinkInterno key={e.href} href={e.href} aria-current={e.atual ? "page" : undefined}>{e.rotulo}</LinkInterno>))}
        </nav>
        {extras && <div className="mtc-lateral__extras">{extras}</div>}
        {usuario && (
          <div className="mtc-lateral__rodape">
            <span className="mtc-conta__inicial" aria-hidden="true">{inicialDe(usuario.nome)}</span>
            <div className="mtc-lateral__quem">
              <strong className="mtc-lateral__nome" title={usuario.nome}>{usuario.nome}</strong>
              <span className="mtc-lateral__papel">{usuario.papel}</span>
            </div>
            {aoSair && <button type="button" className="mtc-botao-icone" aria-label="Sair" title="Sair" onClick={aoSair}><IconeSair tamanho={20} /></button>}
          </div>
        )}
      </aside>
      <main className="mtc-principal">{children}</main>
    </div>
  );
}
